import { IconButton, InputAdornment, TextField } from "@mui/material";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronLeft, TbChevronRight, TbSearch, TbVideoOff, TbX } from "react-icons/tb";

import Panel from "@/layout/Panel";
import PanelTitle from "@/layout/PanelTitle";
import useDriversContext from "@/providers/data/DriversContext";

import DriverCamera from "./components/DriverCamera";

declare global {
    interface Window {
        chrome: boolean;
    }
}

// Each vehicle has 2 streams and the camera server allows max 6 concurrent streams,
// so at most 2 vehicles (4 streams) are mounted at a time. Off-screen pages are unmounted,
// which stops their players and their camera url requests.
const VEHICLES_PER_PAGE = 2;

const easeOut = [0.22, 1, 0.36, 1] as const;

// Page level: slides in the arrow direction (0 = search result, no horizontal move) and staggers its cards.
// Opacity never reaches 0 on exit, so the dark camera frames don't blink against the white background.
const pageVariants = {
    enter: (direction: number) => ({ x: direction * 48 }),
    center: {
        x: 0,
        transition: { duration: 0.4, ease: easeOut, staggerChildren: 0.08 },
    },
    exit: (direction: number) => ({
        x: direction * -48,
        opacity: 0.4,
        scale: 0.98,
        transition: { duration: 0.18, ease: [0.4, 0, 1, 1] as const },
    }),
};

const popVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2, ease: easeOut } },
};

// Each keystroke would remount players and refetch camera urls, so the search is debounced.
const SEARCH_DEBOUNCE_MS = 300;

const normalizePlate = (plate: string) => plate.replace(/[^a-z0-9]/gi, "").toUpperCase();

const usePageVisible = () => {
    const [visible, setVisible] = useState(() => document.visibilityState === "visible");

    useEffect(() => {
        const onChange = () => setVisible(document.visibilityState === "visible");
        document.addEventListener("visibilitychange", onChange);
        return () => document.removeEventListener("visibilitychange", onChange);
    }, []);

    return visible;
};

const CamerasPage = () => {
    const { drivers } = useDriversContext();
    const { t } = useTranslation();
    const pageVisible = usePageVisible();

    const [search, setSearch] = useState("");
    const [plateQuery, setPlateQuery] = useState("");
    const [[page, direction], setPage] = useState<[number, -1 | 0 | 1]>([0, 0]);

    useEffect(() => {
        const timeout = setTimeout(() => setPlateQuery(normalizePlate(search)), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [search]);

    // Only a changed query resets the carousel; typing spaces or re-entering the same plate keeps the page.
    useEffect(() => setPage([0, 0]), [plateQuery]);

    const driversWithVehicle = useMemo(
        () =>
            Object.values(drivers).filter(
                (driver) => driver.vehicle?.id && normalizePlate(driver.vehicle.licensePlate || "").includes(plateQuery)
            ),
        [drivers, plateQuery]
    );

    const pageCount = Math.max(1, Math.ceil(driversWithVehicle.length / VEHICLES_PER_PAGE));
    const currentPage = Math.min(page, pageCount - 1);

    const visibleDrivers = driversWithVehicle.slice(
        currentPage * VEHICLES_PER_PAGE,
        currentPage * VEHICLES_PER_PAGE + VEHICLES_PER_PAGE
    );

    const goTo = useCallback(
        (next: number) => setPage([(next + pageCount) % pageCount, next < currentPage ? -1 : 1]),
        [pageCount, currentPage]
    );

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.key === "ArrowLeft") goTo(currentPage - 1);
            if (e.key === "ArrowRight") goTo(currentPage + 1);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [goTo, currentPage]);

    const isEmpty = driversWithVehicle.length === 0;

    return (
        <MotionConfig reducedMotion="user">
            <Panel title={<PanelTitle title={t("cameras")} />}>
                <div className="flex h-full min-h-0 flex-col gap-3 pb-2">
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: easeOut }}
                        className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-10"
                    >
                        <TextField
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("searchByPlate")}
                            className="w-full sm:w-72"
                            InputProps={{
                                sx: { borderRadius: "9999px", backgroundColor: "white" },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <TbSearch />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <AnimatePresence initial={false}>
                                            {search && (
                                                <motion.div
                                                    variants={popVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    exit="hidden"
                                                >
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t("clear")}
                                                        onClick={() => setSearch("")}
                                                    >
                                                        <TbX />
                                                    </IconButton>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <AnimatePresence>
                            {plateQuery && (
                                <motion.span
                                    variants={popVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                                >
                                    {t("matchingVehicles", { count: driversWithVehicle.length })}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    <AnimatePresence mode="wait" initial={false}>
                        {isEmpty ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.97 }}
                                transition={{ duration: 0.25, ease: easeOut }}
                                className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 text-slate-500"
                            >
                                <motion.div
                                    initial={{ y: 6, rotate: -8 }}
                                    animate={{ y: 0, rotate: 0 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                                >
                                    <TbVideoOff size={28} />
                                </motion.div>
                                {plateQuery ? t("noVehicleFoundForPlate") : t("noVehicleFound")}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="carousel"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex min-h-0 flex-1 items-center gap-2"
                            >
                                <IconButton
                                    aria-label={t("previous")}
                                    disabled={pageCount <= 1}
                                    onClick={() => goTo(currentPage - 1)}
                                >
                                    <TbChevronLeft />
                                </IconButton>

                                <div className="h-full min-h-0 flex-1 overflow-hidden">
                                    {/* mode="wait" mounts the new page only after the old one has left, so streams
                                        never exceed the limit. Cards only animate in; the exit runs on the page. */}
                                    <AnimatePresence mode="wait" initial={false} custom={direction}>
                                        <motion.div
                                            key={`${plateQuery}-${currentPage}`}
                                            custom={direction}
                                            variants={pageVariants}
                                            initial="enter"
                                            animate="center"
                                            exit="exit"
                                            className="grid h-full min-h-0 grid-cols-1 md:grid-cols-2 gap-3"
                                        >
                                            {pageVisible &&
                                                visibleDrivers.map((driver) => (
                                                    <DriverCamera
                                                        key={driver.id}
                                                        driver={driver}
                                                        licensePlate={driver.vehicle?.licensePlate || ""}
                                                    />
                                                ))}
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                <IconButton
                                    aria-label={t("next")}
                                    disabled={pageCount <= 1}
                                    onClick={() => goTo(currentPage + 1)}
                                >
                                    <TbChevronRight />
                                </IconButton>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence initial={false}>
                        {pageCount > 1 && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 8 }}
                                transition={{ duration: 0.25, ease: easeOut }}
                                className="flex shrink-0 items-center justify-center gap-2"
                            >
                                {Array.from({ length: pageCount }, (_, i) => (
                                    <motion.button
                                        key={i}
                                        type="button"
                                        aria-label={`${i + 1} / ${pageCount}`}
                                        onClick={() => goTo(i)}
                                        animate={{ width: i === currentPage ? 24 : 8 }}
                                        whileHover={{ scale: 1.2 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                        className={`h-2 rounded-full ${i === currentPage ? "bg-elitBlue" : "bg-slate-300"}`}
                                    />
                                ))}
                                <span className="ml-2 inline-flex overflow-hidden text-xs text-slate-500">
                                    <AnimatePresence mode="popLayout" initial={false}>
                                        <motion.span
                                            key={currentPage}
                                            initial={{ y: direction >= 0 ? 10 : -10, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            exit={{ y: direction >= 0 ? -10 : 10, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {currentPage + 1}
                                        </motion.span>
                                    </AnimatePresence>
                                    &nbsp;/ {pageCount}
                                </span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </Panel>
        </MotionConfig>
    );
};

export default CamerasPage;
